/* eslint-disable camelcase */
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import placeholderSvg from 'src/new_designs/product-image-placeholder.svg';

// ----------------------------------------------------------------------

const PRIMARY = '#0e7c62';
const ACCENT_LIGHT = '#fef3e0';
const BADGE_HOT = 'rgba(255,107,53,0.9)';
const BADGE_POPULAR = 'rgba(14,124,98,0.9)';
const BADGE_NEW = 'rgba(255,126,179,0.9)';

const TITLE_MIN_PX = 42; // ~2 lines at 16px / 1.3
const DESC_MIN_PX = 40; // ~2 lines at 13px / 1.5

function parseImage(img) {
  if (img && String(img).includes('http')) return img;
  if (img) return `https://syspos.s3.me-central-1.amazonaws.com/media/${img}`;
  return null;
}

function badgeForProduct(status) {
  if (status === 'sale') return { label: 'Hot', bg: BADGE_HOT };
  if (status === 'new') return { label: 'New', bg: BADGE_NEW };
  if (status === 'popular') return { label: 'Popular', bg: BADGE_POPULAR };
  return null;
}

/** Readable menu copy when API returns SHOUTY CASE */
function humanizeMenuText(s) {
  if (!s || typeof s !== 'string') return '';
  const t = s.trim();
  if (t.length > 4 && t === t.toUpperCase()) {
    return t
      .toLowerCase()
      .split(/\s+/)
      .map((w) => (w.length ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(' ');
  }
  return t;
}

OrderV2ProductCard.propTypes = {
  product: PropTypes.object.isRequired,
  currency: PropTypes.string,
  onOpen: PropTypes.func.isRequired,
};

export default function OrderV2ProductCard({ product, currency = 'AED', onOpen }) {
  const theme = useTheme();
  const { name, image, price, actual_price, status, description } = product;
  const src = parseImage(image);
  const badge = badgeForProduct(status);
  const isPopular = status === 'popular';
  const priceNum = actual_price ?? price;
  const priceLabel = `${currency} from ${Number(priceNum).toFixed(0)}`;

  const prefersReduced =
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const displayName = humanizeMenuText(name);
  const displayDesc = description ? humanizeMenuText(description) : '';

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(product)}
      sx={{
        cursor: 'pointer',
        textAlign: 'left',
        width: 1,
        maxWidth: '100%',
        minWidth: 0,
        minHeight: 0,
        height: 1,
        m: 0,
        background: 'transparent',
        fontFamily: 'inherit',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        transition: prefersReduced ? 'none' : 'transform 0.2s ease, box-shadow 0.2s ease',
        borderRadius: '22px',
        bgcolor: '#fff',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.04)',
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
        p: 1.25,
        overflow: 'hidden',
        '@media (hover: hover)': {
          '&:hover': {
            transform: prefersReduced ? 'none' : 'translateY(-2px)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          },
          '&:hover .ov2-card-img': {
            transform: prefersReduced ? 'none' : 'scale(1.05)',
          },
        },
        '&:focus-visible': {
          outline: `2px solid ${PRIMARY}`,
          outlineOffset: 3,
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          flexShrink: 0,
          borderRadius: '20px',
          overflow: 'hidden',
          aspectRatio: '1 / 1',
          bgcolor: '#fafafa',
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {badge && (
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              zIndex: 2,
              fontSize: 11,
              fontWeight: 600,
              px: 1.5,
              py: 0.5,
              borderRadius: '28px',
              textTransform: 'uppercase',
              letterSpacing: '0.02em',
              color: '#fff',
              bgcolor: badge.bg,
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}
          >
            {badge.label}
          </Box>
        )}
        <Box
          component="img"
          src={src || placeholderSvg}
          alt=""
          className="ov2-card-img"
          sx={{
            width: 1,
            height: 1,
            objectFit: 'contain',
            objectPosition: 'center',
            display: 'block',
            transition: prefersReduced ? 'none' : 'transform 0.3s ease',
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          pt: 1.5,
          px: 0.25,
          pb: 0.25,
        }}
      >
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 600,
            lineHeight: 1.3,
            color: '#1d1d1f',
            minHeight: TITLE_MIN_PX,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textTransform: 'none',
            letterSpacing: '-0.01em',
          }}
        >
          {displayName}
        </Typography>

        <Typography
          sx={{
            fontSize: 13,
            color: '#86868b',
            lineHeight: 1.5,
            minHeight: DESC_MIN_PX,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textTransform: 'none',
            letterSpacing: 0,
            mb: 0,
          }}
        >
          {displayDesc || '\u00a0'}
        </Typography>

        <Box sx={{ mt: 'auto', pt: 1 }}>
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              maxWidth: '100%',
              px: '14px',
              py: '6px',
              borderRadius: '20px',
              fontSize: 14,
              fontWeight: 700,
              bgcolor: isPopular ? PRIMARY : ACCENT_LIGHT,
              color: isPopular ? '#fff' : '#1d1d1f',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              verticalAlign: 'top',
            }}
          >
            {priceLabel}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
